/* oxlint-disable react/button-has-type */
import { type Component, type ComponentProps, Show, splitProps } from "solid-js";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@time-tracker/shared";
import { Loader2 } from "@fleet/icons";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[transform,background-color,border-color,color,filter] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus:brightness-110 focus:scale-[1.02] hover:brightness-125 hover:scale-[1.02] disabled:pointer-events-none disabled:opacity-50 cursor-pointer active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-accent/15",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-primary/10 hover:text-primary",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-primary/10 hover:text-primary focus:bg-primary/15 focus:text-primary focus-visible:bg-primary/15 focus-visible:text-primary",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    /**
     * When `true`, the button shows a spinner, is `disabled` (no double-submit)
     * and exposes `aria-busy` for assistive tech. Pair with a `loading` label
     * (e.g. "Signing in…") in `children` for full feedback.
     */
    loading?: boolean;
  };

const Button: Component<ButtonProps> = (props) => {
  const [local, rest] = splitProps(props, [
    "class",
    "variant",
    "size",
    "type",
    "loading",
    "disabled",
    "children",
  ]);

  return (
    <button
      type={local.type ?? "button"}
      class={cn(buttonVariants({ variant: local.variant, size: local.size }), local.class)}
      disabled={local.disabled || local.loading}
      aria-busy={local.loading ? "true" : undefined}
      {...rest}
    >
      <Show when={local.loading}>
        <Loader2 class="h-4 w-4 animate-spin" aria-hidden="true" />
      </Show>
      {local.children}
    </button>
  );
};

export { Button, buttonVariants };
